import { useEffect, useState } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import {
  requestStudyNoteLearning,
  type NoteAnnotationSelection,
} from './studyNotebookOperationsApi';
import type { StudyPdfAnnotation, StudyDocumentDetail } from './studyApi';

export function StudyNoteAnnotationInputs({
  documents,
  selected,
  onChange,
  disabled,
}: {
  documents: string[];
  selected: NoteAnnotationSelection[];
  onChange: (items: NoteAnnotationSelection[]) => void;
  disabled: boolean;
}) {
  const owner = useAuthStore(() => getKidSub());
  const [documentId, setDocumentId] = useState(documents[0] ?? '');
  const [page, setPage] = useState(1);
  const detail = useQuery({
    queryKey: ['study-note-annotation-document', owner, documentId],
    queryFn: () =>
      requestStudyNoteLearning<StudyDocumentDetail>(
        `/study/documents/${encodeURIComponent(documentId)}`,
      ),
    enabled: Boolean(owner && documentId),
    retry: false,
  });
  const pdf = detail.data?.mime_type === 'application/pdf';
  const supported = pdf || Boolean(detail.data?.mime_type.startsWith('image/'));
  const annotations = useInfiniteQuery({
    queryKey: [
      'study-note-annotation-inputs',
      owner,
      documentId,
      page,
      detail.data?.source_revision,
    ],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      requestStudyNoteLearning<{ items: StudyPdfAnnotation[]; next_cursor: string | null }>(
        `/study/documents/${encodeURIComponent(documentId)}/${pdf ? 'pdf' : 'image'}-annotations?page_number=${pdf ? page : 1}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(owner && supported),
    retry: false,
    staleTime: 0,
  });
  useEffect(() => {
    setPage(1);
  }, [documentId]);
  return (
    <fieldset className="my-3 space-y-2 text-sm" disabled={disabled}>
      <legend className="font-bold">Include saved page marks (optional)</legend>
      <p>Choose the exact saved versions. Unselected marks are not included.</p>
      <select
        aria-label="Annotation source file"
        className="input-k12"
        value={documentId}
        onChange={(e) => setDocumentId(e.target.value)}
      >
        {documents.map((id, i) => (
          <option key={id} value={id}>
            Material {i + 1}
          </option>
        ))}
      </select>
      {pdf && (
        <label>
          Page{' '}
          <input
            className="input-k12 w-20"
            type="number"
            min={1}
            max={5000}
            value={page}
            onChange={(e) => setPage(Math.max(1, Number(e.target.value) || 1))}
          />
        </label>
      )}
      {!supported && detail.data && <p>This file does not have PDF/image page marks.</p>}
      {annotations.isFetching && <p role="status">Loading saved marks…</p>}
      {(detail.error || annotations.error) && (
        <p role="alert">
          Page marks could not load.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              void detail.refetch();
              void annotations.refetch();
            }}
          >
            Retry
          </button>
        </p>
      )}
      {annotations.data?.pages
        .flatMap((chunk) => chunk.items)
        .map((item) => {
          const chosen = selected.some((ref) => ref.id === item.id && ref.version === item.version);
          return (
            <label key={item.id} className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={chosen}
                disabled={!chosen && selected.length >= 20}
                onChange={(e) =>
                  onChange([
                    ...selected.filter((ref) => ref.id !== item.id),
                    ...(e.target.checked
                      ? [
                          {
                            id: item.id,
                            version: item.version,
                            document_id: documentId,
                            source_revision: item.source_revision,
                          },
                        ]
                      : []),
                  ])
                }
              />
              {item.kind === 'note' ? item.body : item.kind === 'ink' ? 'Handwriting' : 'Highlight'}{' '}
              · page {item.page_number} · saved v{item.version}
            </label>
          );
        })}
      {annotations.hasNextPage && (
        <button
          className="btn-pill-secondary"
          type="button"
          disabled={annotations.isFetchingNextPage}
          onClick={() => void annotations.fetchNextPage()}
        >
          Load more saved marks
        </button>
      )}
      {selected.length > 0 && (
        <p>
          {selected.length} saved marks selected.{' '}
          <button className="underline" type="button" onClick={() => onChange([])}>
            Clear selection
          </button>
        </p>
      )}
    </fieldset>
  );
}
